import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as d3 from 'd3';
import _ from 'lodash';
import Queue from 'queue';
import { forkJoin } from 'rxjs';

import { AuthenticationService } from '../../services/auth/authentication.service';
import { BusService } from '../../services/bus.service';
import { EventsService } from '../../services/events.service';
import { SessionsRequestService } from '../../services/sessions/sessions.request.service';
import { TopicvizService } from '../../services/topicviz/topicviz.service';
import { ClusterManagerModalComponent } from './cluster-manager-modal/cluster-manager-modal.component';
import ClusterData from './ClusterData';
import Constants from './Constants';
import DataProcessing from './DataProcessing';
import { drawGridPoints } from './drawingUtils';
import { EditClusterModalComponent } from './edit-cluster-modal/edit-cluster-modal.component';
import { ExternalDocumentModalComponent } from './external-document-modal/external-document-modal.component';
import ImageRegistry from './ImageRegistry';
import ForceLayout from './Layout/ForceLayout';
import createDocumentGroupLevel from './levels/createDocumentGroupLevel';
import createDocumentLevel from './levels/createDocumentLevel';
import createSubTopicLevel from './levels/createSubTopicLevel';
import createTopicLayer from './levels/createTopicLayer';
import createWordLevel from './levels/createWordLevels';
import RenderingState from './RenderingState';
import TopicVizScaling from './TopicVizScaling';
import ViewLevelManager from './ViewLevelManager';
import VirtualDom from './VirtualDom/VirtualDom';

@Component({
  selector: 'app-topicviz-panel',
  templateUrl: './topicviz-panel.component.html',
  styleUrls: ['./topicviz-panel.component.scss'],
})
export class TopicvizPanelComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true })
  canvas: ElementRef<HTMLCanvasElement>;

  @ViewChild('canvasParent', { static: true })
  canvasParent: ElementRef;

  private ctx: CanvasRenderingContext2D;
  private customParentElement: HTMLElement;

  private scaling;

  private canvasWidth: number;
  private canvasHeight: number;
  private downloadedImages = {};
  private virtualDom = new VirtualDom();
  private downloadQueue: any;
  isLoading = true;
  hasError = false;
  private mouseDown: boolean;
  private registeredImagesByUrl = {};

  private onResizeDebounced: any;
  private debouncedMouseMove: any;
  private zoom: any;
  private imageRegistry: ImageRegistry;
  public isFullScreen = false;
  public isEditMode = false;
  private mouseMoveEventListener: any;
  private mouseClickEventListener: any;
  private viewLevelManager: ViewLevelManager;
  private renderingState: RenderingState;
  private clusterData: ClusterData;
  currentConfiguationId: any;

  constructor(
    private sessionsRequestService: SessionsRequestService,
    private events: EventsService,
    private bus: BusService,
    private topicVizDataLoader: TopicvizService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    public auth: AuthenticationService,
  ) {
    this.downloadQueue = new Queue({
      concurrency: Constants.general.maxConcurrentDownloads,
    });
    this.downloadQueue.start();
    this.imageRegistry = new ImageRegistry();
    this.onResizeDebounced = _.debounce(this._onResize, 250);
    this.debouncedMouseMove = _.throttle(this.onMouseMove, 50);
    this.renderingState = new RenderingState();
    this.clusterData = new ClusterData();
  }

  openIdea(ideaId, sessionId) {
    this.bus.publish(
      this.events.requested.data.session.idea.singleWithSession,
      ideaId,
      sessionId,
    );
  }

  openClusterManagerModal() {
    const modalRef = this.modalService.open(ClusterManagerModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modalRef.componentInstance.setCurrentConfigurationId(
      this.currentConfiguationId,
    );
  }

  openTopicEditModal(selectedClusterIndex = 0) {
    const modalRef = this.modalService.open(EditClusterModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modalRef.componentInstance.setClusters(
      this.clusterData.getClusterInfoData(),
      selectedClusterIndex,
    );
    modalRef.result.then(result => {
      if (result) {
        this.reset();
        this.init();
      }
    });
  }

  reset() {
    this.virtualDom = new VirtualDom();
  }

  setCanvasWidthHeight(width, height) {
    this.canvasWidth = width;
    this.canvasHeight = height;
  }

  getCurrentZoomLevel() {
    const zoomStep = Constants.zoom.max / Constants.zoom.maxSteps;
    return Math.floor(this.renderingState.getCurrentTransform().k / zoomStep);
  }

  getCanvasWidthHeight() {
    return [this.canvasWidth, this.canvasHeight];
  }

  getCtx() {
    return this.ctx;
  }

  draw() {
    if (this.renderingState.getDirtyBit()) {
      const ctx = this.getCtx();
      ctx.globalAlpha = 1;
      ctx.beginPath();

      const transform = this.renderingState.getCurrentTransform();

      // clear canvas
      const [width, height] = this.getCanvasWidthHeight();
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = 'rgb(67,66,69)';

      const zoomLevel = this.viewLevelManager.getDynamicZoomLevel(transform);

      if (this.viewLevelManager.showGridPoints(transform)) {
        drawGridPoints(
          this.getCtx(),
          transform,
          this.scaling.getScaleFactor() * Constants.grid.offset * 1.5,
          this.scaling.getScaleFactor() * Constants.grid.width * 0.7,
          width,
          height,
          this.getViewPort(),
        );
        ctx.globalAlpha = 1;
      }
      this.virtualDom.draw(
        this.getViewPort(),
        zoomLevel,
        ctx,
        transform,
        this.scaling,
        this.renderingState,
      );

      this.renderingState.setDirtyBit(false);
    }

    window.requestAnimationFrame(() => this.draw());
  }

  drawDocumentCards(drawTitle = true) {
    const ctx = this.getCtx();
    const transform = this.renderingState.getCurrentTransform();

    ctx.fillStyle = '#000';

    for (const documentCard of this.virtualDom
      .getLayer('documentCards')
      .getElementsInViewPort(this.getViewPort())) {
      this.resetCtx();

      documentCard.draw(ctx, transform, this.scaling, drawTitle);
    }

    this.resetCtx();
  }

  drawDocumentGroups() {
    const ctx = this.getCtx();
    const transform = this.renderingState.getCurrentTransform();

    ctx.fillStyle = '#000';

    for (const documentCard of this.virtualDom
      .getLayer('documentGroups')
      .getElementsInViewPort(this.getViewPort())) {
      this.resetCtx();

      documentCard.draw(ctx, transform, this.scaling);
    }

    this.resetCtx();
  }

  getScaleFactor() {
    return this.scaling.getScaleFactor();
  }

  drawSubTopics(opacity = 1) {
    const transform = this.renderingState.getCurrentTransform();
    const ctx = this.getCtx();

    for (const subTopicElement of this.virtualDom
      .getLayer('subTopics')
      .getElementsInViewPort(this.getViewPort())) {
      subTopicElement.draw(ctx, transform, this.scaling, this.imageRegistry, {
        opacity: opacity,
      });
    }
  }

  resetCtx() {
    const ctx = this.getCtx();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  drawTopics(opacity = 1) {
    this.resetCtx();
    const transform = this.renderingState.getCurrentTransform();
    const ctx = this.getCtx();

    for (const topicElement of this.virtualDom
      .getLayer('topics')
      .getElements()) {
      topicElement.draw(ctx, transform, this.scaling, this.imageRegistry, {
        opacity: opacity,
      });
    }
  }

  loadData({
    sourceDataset = null,
    targetDataset = null,
    clusteringAlgorithm = null,
    clusterSize = null,
    imageType = null,
    dimensionalityReduction = null,
    spatialCollectionId = null,
  } = {}) {
    const clusterInfoObservable = this.topicVizDataLoader.getClusterInfo(
      sourceDataset,
      targetDataset,
      clusteringAlgorithm,
      clusterSize,
      imageType,
      spatialCollectionId,
    );

    const documentDataObservable = this.topicVizDataLoader.getDocumentData(
      sourceDataset,
      targetDataset,
      clusteringAlgorithm,
      clusterSize,
      dimensionalityReduction,
      imageType,
      spatialCollectionId,
    );
    const allSessionsRequest = this.sessionsRequestService.getAll();

    forkJoin([
      clusterInfoObservable,
      documentDataObservable,
      allSessionsRequest,
    ]).subscribe(
      arr => {
        const clusterInfo = arr[0];
        const documentData = arr[1];
        const allSessions = arr[2];

        const filteredDocumentPoints = DataProcessing.filterDocumentPoints({
          sessions: allSessions,
          documentPoints: documentData['data'],
        });
        const documentPoints = DataProcessing.processDocumentData(
          filteredDocumentPoints,
        );

        this.currentConfiguationId = documentData['id'];
        this.clusterData.updateDocumentPoints(documentPoints);
        this.clusterData.setDocumentInfoData(documentPoints);
        this.clusterData.setClusterInfoData(clusterInfo);

        this.createView();
        this.initZoom();
        this.renderingState.setCurrentTransform(d3.zoomIdentity);
        this.draw();
      },
      () => {
        this.isLoading = false;
        alert('Error while loading Clustering');
        this.hasError = true;
      },
    );
  }

  hasWordElements(): boolean {
    //checks if "wordElement" mode is on (show words instead of topic labels)
    return this.clusterData
      .getDocumentInfoData()
      .some(dataPoint => dataPoint.getSource() === 'word');
  }

  createView() {
    const documentDataPoints = this.createDocumentLayout(
      this.clusterData.getDocumentInfoData(),
    );
    createDocumentLevel(
      documentDataPoints,
      this.virtualDom,
      this.scaling,
      this.imageRegistry,
      this.renderingState,
      this.clusterData,
    );

    if (this.hasWordElements()) {
      //Kmeans with word level
      if (Constants.kmeansClustering.showWords) {
        createWordLevel(
          documentDataPoints,
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
          this.renderingState,
        );
      }
      if (Constants.kmeansClustering.showTopics) {
        createTopicLayer(
          this.clusterData.getClusterInfoData(),
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
        );
      }
      if (Constants.kmeansClustering.showGroupedDocuments) {
        createDocumentGroupLevel(
          this.clusterData,
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
          this.renderingState,
          Constants.kmeansClustering.showGroupedDocumentLabels,
        );
      }

      if (Constants.kmeansClustering.showSubTopics) {
        createSubTopicLevel(
          this.clusterData.getClusterInfoData(),
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
          this.renderingState,
        );
      }
    } else {
      createTopicLayer(
        this.clusterData.getClusterInfoData(),
        this.virtualDom,
        this.scaling,
        this.imageRegistry,
      );
      if (Constants.subTopics.show) {
        createSubTopicLevel(
          this.clusterData.getClusterInfoData(),
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
          this.renderingState,
        );
      }
      if (Constants.groupedDocuments.show) {
        createDocumentGroupLevel(
          this.clusterData,
          this.virtualDom,
          this.scaling,
          this.imageRegistry,
          this.renderingState,
          Constants.groupedDocuments.showLabel,
        );
      }
    }

    this.setLoading(false);
    this.renderingState.setDirtyBit(true);
  }

  setLoading(value) {
    this.isLoading = value;
  }

  createDocumentLayout(documentDataPoints) {
    const [width, height] = this.getCanvasWidthHeight();
    const forceLayout = new ForceLayout(documentDataPoints);
    documentDataPoints = forceLayout.applyCollisionsAndRepulsion();

    const radius = forceLayout.getCollisionRadius();
    const radiusOnScreen = radius * Math.sqrt(width * height);
    this.scaling = new TopicVizScaling(documentDataPoints, width, height);

    const scaleFactor =
      (Constants.documentPoint.scaleFactor * radiusOnScreen) /
      Constants.documentPoint.width;
    this.scaling.setScaleFactor(scaleFactor);

    this.viewLevelManager = new ViewLevelManager({
      minDocumentCardThreshold: Constants.thresholds.documentCard[0],
      minDocumentTitleThreshold: Constants.thresholds.documentTitle[0],
      subTopicMinThreshold: Constants.thresholds.subTopic[0],
      subTopicMaxThreshold: Constants.thresholds.subTopic[1],
      topicMaxThreshold: Constants.thresholds.topic[1],
      documentPointMaxThreshold: Constants.thresholds.documentPoints[1],
      documentPointMinThreshold: Constants.thresholds.documentPoints[0],
      scaleFactor: scaleFactor,
    });

    return documentDataPoints;
  }

  init() {
    this.route.queryParams.subscribe(params => {
      this.resizeCanvasToParent();
      this.ctx = this.canvas.nativeElement.getContext('2d');
      this.customParentElement = document.createElement('custom');
      this.loadData(params);
    });
  }

  ngOnInit() {
    document.body.scrollTop = 0;

    // temporarily disable scroll (hides the footer)
    document.body.style.overflowY = 'hidden';
    this.init();
  }

  initZoom() {
    const [canvasWidth, canvasHeight] = this.getCanvasWidthHeight();
    this.zoom = d3
      .zoom()
      .scaleExtent([
        Constants.zoom.min,
        (Constants.zoom.max * 1) / this.getScaleFactor(),
      ])
      .translateExtent([
        [-canvasWidth * 0.25, -canvasHeight * 0.25],
        [canvasWidth * 1.25, canvasHeight * 1.25],
      ])
      .on('zoom', ({ transform }) =>
        this.renderingState.setCurrentTransform(transform),
      )
      .on('start', this.onDragStart)
      .on('end', this.onDragEnd);

    d3.select(this.ctx.canvas).call(this.zoom);
  }

  onDragStart() {
    this.mouseDown = true;
  }

  onDragEnd() {
    this.mouseDown = true;
  }

  // is called debounced from _onResize
  _onResize() {
    this.setLoading(true);
    this.resizeCanvasToParent();

    setTimeout(() => {
      this.reset();
      this.createView();
      this.initZoom();
    }, 10);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.onResizeDebounced();
  }

  getViewPort() {
    const transform = this.renderingState.getCurrentTransform();
    const [canvasWidth, canvasHeight] = this.getCanvasWidthHeight();

    const xmin = -transform.x / transform.k;
    const xmax = xmin + canvasWidth / transform.k;
    const ymin = -transform.y / transform.k;
    const ymax = ymin + canvasHeight / transform.k;

    return { xmin: xmin, xmax: xmax, ymin: ymin, ymax: ymax };
  }

  onMouseClick(event: any) {
    event.preventDefault();
    if (this.mouseDown) {
      return;
    }
    const [mouseX, mouseY] = [event.offsetX, event.offsetY];
    const transform = this.renderingState.getCurrentTransform();

    if (this.viewLevelManager.showDocumentCards(transform)) {
      if (this.viewLevelManager.showDocumentCards(transform)) {
        for (const element of this.virtualDom
          .getLayer('documentCards')
          .getElementsInViewPort(this.getViewPort())) {
          if (element.isRayTraced(mouseX, mouseY, transform)) {
            this.onDocumentCardClicked(element);
            this.renderingState.setDirtyBit(true);
            break;
          }
        }
      }
    }

    if (this.isEditMode) {
      for (const element of this.virtualDom
        .getLayer('topicElements')
        .getElementsInViewPort(this.getViewPort())) {
        if (element.isRayTraced(mouseX, mouseY, transform)) {
          this.openTopicEditModal(element.get('clusterIndex'));
          return; // prevent opening modal twice for subtopic and topic
        }
      }

      for (const element of this.virtualDom
        .getLayer('subTopicElements')
        .getElementsInViewPort(this.getViewPort())) {
        if (element.isRayTraced(mouseX, mouseY, transform)) {
          this.openTopicEditModal(element.get('clusterIndex'));
        }
      }
    }
  }

  openExternalDocument(id) {
    this.topicVizDataLoader.loadDocumentInfo(id, 'sm', 'photo').subscribe(
      response => {
        const modalRef = this.modalService.open(
          ExternalDocumentModalComponent,
          { size: 'lg', windowClass: 'big-modal' },
        );
        modalRef.componentInstance.setDocumentInfo(response);
        modalRef.result.then(result => {
          if (result) {
            this.reset();
            this.init();
          }
        });
      },
      error => {
        alert('Failed to load data: ' + error['message']);
      },
    );
  }

  onDocumentCardClicked(documentCard) {
    const documentPoint = this.clusterData.getDocumentPointById(
      documentCard.getDocumentId(),
    );
    if (this.isFullScreen) {
      this.toggleFullScreen();
    }

    if (!this.isEditMode && documentPoint.getSource() == 'idea') {
      this.openIdea(documentPoint.getIdeaId(), documentPoint.getSessionId());
    } else {
      this.openExternalDocument(documentPoint.getId());
    }
  }

  onMouseMove(event: any) {
    if (this.hasError || this.isLoading) {
      return;
    }

    event.preventDefault();
    if (
      this.mouseDown ||
      !this.viewLevelManager.showDocumentCards(
        this.renderingState.getCurrentTransform(),
      )
    ) {
      return;
    }
    const [mouseX, mouseY] = [event.offsetX, event.offsetY];

    const transform = this.renderingState.getCurrentTransform();

    for (const element of this.virtualDom
      .getLayer('documentCards')
      .getElementsInViewPort(this.getViewPort())) {
      if (element.isRayTraced(mouseX, mouseY, transform)) {
        if (!element.getAttribute('mouseOverload')) {
          this.canvasParent.nativeElement.style.cursor = 'pointer';
          element.setAttribute('mouseOver', true);
          element.animateAttribute(
            'scale',
            Constants.selection.scaleFactorSelection,
            100,
          );
          this.renderingState.setDirtyBit(true);
        }
      } else if (element.getAttribute('mouseOver')) {
        this.canvasParent.nativeElement.style.cursor = 'default';

        element.setAttribute('mouseOver', false);
        element.animateAttribute('scale', 1, 100);

        this.renderingState.setDirtyBit(true);
      }
    }
  }

  resizeCanvasToParent() {
    this.canvas.nativeElement.width =
      this.canvasParent.nativeElement.offsetWidth;
    this.canvas.nativeElement.height =
      this.canvasParent.nativeElement.offsetHeight;

    this.subscribeCanvasEventListeners();

    this.setCanvasWidthHeight(
      Math.max(
        Constants.general.minCanvasWidth,
        this.canvasParent.nativeElement.offsetWidth,
      ),
      Math.max(
        Constants.general.minCanvasHeight,
        this.canvasParent.nativeElement.offsetHeight,
      ),
    );
  }

  subscribeCanvasEventListeners() {
    if (this.mouseMoveEventListener) {
      this.canvas.nativeElement.removeEventListener(
        'mousemove',
        this.mouseMoveEventListener,
        true,
      );
    }
    if (this.mouseClickEventListener) {
      this.canvas.nativeElement.removeEventListener(
        'click',
        this.mouseClickEventListener,
        true,
      );
    }

    this.mouseMoveEventListener = this.debouncedMouseMove.bind(this);
    this.mouseClickEventListener = this.onMouseClick.bind(this);

    this.canvas.nativeElement.addEventListener(
      'mousemove',
      this.mouseMoveEventListener,
      true,
    );
    this.canvas.nativeElement.addEventListener(
      'click',
      this.mouseClickEventListener,
      true,
    );
  }

  onZoomInButtonClicked() {
    d3.select(this.ctx.canvas)
      .transition()
      .duration(Constants.zoom.zoomTransitionTime)
      .call(this.zoom.scaleBy, Constants.zoom.zoomButtonIncrement);
  }

  onZoomOutButtonClicked() {
    d3.select(this.ctx.canvas)
      .transition()
      .duration(Constants.zoom.zoomTransitionTime)
      .call(this.zoom.scaleBy, 1 / Constants.zoom.zoomButtonIncrement);
  }

  openFullscreen() {
    if ('requestFullscreen' in this.canvasParent.nativeElement) {
      this.canvasParent.nativeElement['requestFullscreen']();
    } else if ('mozRequestFullScreen' in this.canvasParent.nativeElement) {
      /* Firefox */
      this.canvasParent.nativeElement['mozRequestFullScreen']();
    } else if ('webkitRequestFullscreen' in this.canvasParent.nativeElement) {
      /* Chrome, Safari and Opera */
      this.canvasParent.nativeElement['webkitRequestFullscreen']();
    } else if ('msRequestFullscreen' in this.canvasParent.nativeElement) {
      /* IE/Edge */
      this.canvasParent.nativeElement['msRequestFullscreen']();
    }
  }

  /* Close fullscreen */
  closeFullscreen() {
    if ('exitFullScreen' in document) {
      document['exitFullscreen']();
    } else if ('mozCancelFullScreen' in document) {
      /* Firefox */
      (document as any)['mozCancelFullScreen']();
    } else if ('webkitExitFullscreen' in document) {
      /* Chrome, Safari and Opera */
      (document as any)['webkitExitFullscreen']();
    } else if ('msExitFullscreen' in document) {
      /* IE/Edge */
      (document as any)['msExitFullscreen']();
    }
  }

  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
  }

  toggleFullScreen() {
    if (this.isFullScreen) {
      this.closeFullscreen();
      this.isFullScreen = false;
    } else {
      this.openFullscreen();
      this.isFullScreen = true;
    }
  }

  ngOnDestroy() {
    // re-activate scroll on body
    document.body.style.overflowY = '';
  }
}
